// screens/marketplace/MyEnrollments.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  GraduationCap,
  ChevronRight,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  BookOpen,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../providers/ToastProvider";
import {
  listMyEnrollments,
  enrollmentStatusMeta,
  isPaid,
} from "./coursesApi";

const money = (v) => `GH₵${Number(v || 0).toFixed(2)}`;

const toneColors = (colors, tone) => {
  const map = {
    success: { fg: colors.success, bg: colors.successBg, icon: CheckCircle2 },
    warning: { fg: colors.warning, bg: colors.warningBg, icon: Clock },
    error: { fg: colors.error, bg: colors.errorBg, icon: XCircle },
    muted: { fg: colors.secondaryText, bg: colors.hoverBg, icon: AlertCircle },
  };
  return map[tone] || map.muted;
};

export default function MyEnrollments() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { authFetch, isAuthenticated } = useAuth();
  const { colors } = useTheme();
  const { showToast } = useToast();

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listMyEnrollments(authFetch);
      setEnrollments(data);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [authFetch, showToast]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login?next=/courses/enrollments");
      return;
    }
    load();
  }, [isAuthenticated, load, navigate]);

  return (
    <div style={styles.pageWrap}>
      <header style={styles.header}>
        <h1 style={styles.h1(colors)}>My courses</h1>
        <p style={styles.subtitle(colors)}>
          Courses you've purchased. Telegram invites appear here once payment
          confirms.
        </p>
      </header>

      {loading ? (
        <div style={styles.loadingWrap(colors)}>
          <div style={styles.spinner(colors)} />
          <span>Loading your enrollments…</span>
        </div>
      ) : enrollments.length === 0 ? (
        <EmptyState
          colors={colors}
          onBrowse={() => navigate("/courses")}
        />
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {enrollments.map((e) => (
            <EnrollmentCard
              key={e.id}
              enrollment={e}
              colors={colors}
              justPaid={params.get("just_paid") === "1"}
              onOpen={() => navigate(`/courses/enrollments/${e.id}`)}
            />
          ))}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Card
   ══════════════════════════════════════════════════════════ */

const EnrollmentCard = ({ enrollment, colors, justPaid, onOpen }) => {
  const meta = enrollmentStatusMeta(enrollment.status);
  const tone = toneColors(colors, meta.tone);
  const Icon = tone.icon;
  const paid = isPaid(enrollment);

  return (
    <button
      type="button"
      onClick={onOpen}
      style={styles.card(colors, paid)}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = colors.btnBg;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = paid
          ? `${colors.success}55`
          : colors.border;
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div style={styles.cardIcon(colors)}>
        <GraduationCap size={22} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.cardTitle(colors)}>{enrollment.course_title}</div>
        <div style={styles.cardMeta(colors)}>
          <span style={styles.tonePill(tone.fg, tone.bg)}>
            <Icon size={11} />
            {meta.label}
          </span>
          <span>{money(enrollment.amount)}</span>
          {enrollment.payment_method && (
            <span>· {enrollment.payment_method}</span>
          )}
        </div>
        {paid && enrollment.telegram_link && (
          <div style={styles.telegramHint(colors)}>
            <Send size={12} />
            Telegram invite ready — open to join
          </div>
        )}
        {!paid && (
          <div style={styles.pendingHint(colors)}>
            {enrollment.status === "FAILED"
              ? "Payment failed. Enroll again to retry."
              : "Waiting for payment confirmation."}
          </div>
        )}
      </div>
      <ChevronRight size={18} color={colors.secondaryText} />
    </button>
  );
};

const EmptyState = ({ colors, onBrowse }) => (
  <div style={styles.emptyWrap(colors)}>
    <div style={styles.emptyIcon(colors)}>
      <BookOpen size={26} />
    </div>
    <h3 style={styles.emptyTitle(colors)}>No courses yet</h3>
    <p style={styles.emptyText(colors)}>
      When you enroll in a course, it shows up here with your Telegram invite.
    </p>
    <button type="button" onClick={onBrowse} style={styles.emptyPrimary(colors)}>
      Browse courses
    </button>
  </div>
);

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  pageWrap: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "20px 16px 60px",
  },
  header: {
    marginBottom: 24,
  },
  h1: (c) => ({
    margin: 0,
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: "-0.02em",
    color: c.heading,
  }),
  subtitle: (c) => ({
    margin: "6px 0 0",
    fontSize: 13.5,
    color: c.secondaryText,
    lineHeight: 1.55,
    maxWidth: 560,
  }),

  card: (c, paid) => ({
    display: "flex",
    alignItems: "center",
    gap: 14,
    width: "100%",
    padding: 16,
    borderRadius: 14,
    border: `1px solid ${paid ? `${c.success}55` : c.border}`,
    background: c.raisedBg,
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    transition: "transform 0.15s ease, border-color 0.15s ease",
  }),
  cardIcon: (c) => ({
    width: 46,
    height: 46,
    borderRadius: 12,
    background: `${c.btnBg}18`,
    color: c.btnBg,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  cardTitle: (c) => ({
    fontSize: 15,
    fontWeight: 800,
    color: c.heading,
    marginBottom: 6,
    letterSpacing: "-0.01em",
  }),
  cardMeta: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12.5,
    color: c.secondaryText,
    flexWrap: "wrap",
  }),
  tonePill: (fg, bg) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    padding: "2px 8px",
    borderRadius: 999,
    color: fg,
    background: bg,
  }),
  telegramHint: (c) => ({
    marginTop: 8,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 700,
    color: c.success,
  }),
  pendingHint: (c) => ({
    marginTop: 8,
    fontSize: 12,
    color: c.secondaryText,
  }),

  loadingWrap: (c) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 260,
    gap: 12,
    color: c.secondaryText,
  }),
  spinner: (c) => ({
    width: 32,
    height: 32,
    border: `3px solid ${c.border}`,
    borderTopColor: c.btnBg,
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  }),

  emptyWrap: (c) => ({
    textAlign: "center",
    padding: "60px 24px",
    borderRadius: 16,
    border: `1px dashed ${c.border}`,
    background: c.raisedBg,
  }),
  emptyIcon: (c) => ({
    width: 64,
    height: 64,
    borderRadius: 18,
    background: c.hoverBg,
    color: c.secondaryText,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  }),
  emptyTitle: (c) => ({
    margin: "0 0 6px",
    fontSize: 17,
    fontWeight: 800,
    color: c.heading,
  }),
  emptyText: (c) => ({
    margin: "0 auto 22px",
    fontSize: 13.5,
    color: c.secondaryText,
    maxWidth: 420,
    lineHeight: 1.55,
  }),
  emptyPrimary: (c) => ({
    padding: "11px 20px",
    borderRadius: 10,
    border: "none",
    background: c.btnBg,
    color: c.btnText,
    fontWeight: 700,
    fontSize: 13.5,
    cursor: "pointer",
  }),
};